<script setup lang="ts">
import IndexLayout from "~/components/Indexs/IndexLayout.vue";
import IndexLayoutSkeleton from "~/components/Skeleton/IndexLayoutSkeleton.vue";
import { usePagination } from "~/composables/paginations/usePagination";
import { fetchWithStateHandling } from "~/composables/utils/useStateManager";
import type { IPaginationApi } from "~/utils/interfaces/table-interfaces";
import { PaginationKeysEnum } from "~/utils/enums/pagination-keys.enums";
import { useLearningPathAttendees } from "~/composables/learning-path/useLearningPathAttendees";
import AttendeeJourneyView from "./view/AttendeeJourneeView.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import { useSessionIndividual } from "~/composables/sessions/useSessionIndividual";
import { ItemType } from "@simbelapp/order-sdk";
import SessionAttendeeDetailsModal from "~/components/Pages/training-plan/session-sidemodal/SessionAttendeeDetailsModal.vue";
import { useSessionsStore } from "~/store/sessions/sessions-store";
import { useLearningPathAttendeeStore } from "~/store/learning-path/learning-path-attendee.store";

const maxHeight = ref<string>("calc(100vh - 300px)");
const { headersJourney, getNav } = useLearningPathAttendees();
const paginate = usePagination(PaginationKeysEnum.LEARNING_PATH_ATTENDEE_JOURNEY_PAGINATION);
const learningPathAttendeesStore = useLearningPathAttendeeStore();
const { fetchLearningPathAttendeeJourneyFullDatas } = learningPathAttendeesStore;
const { learningPathAttendeeJourneyFullDatas } = storeToRefs(learningPathAttendeesStore);
const indexGrid = ref();
const isLoadingRef = ref(true);
const isErrorRef = ref(true);
const visibleHeaders = computed(() => headersJourney.value.filter((h) => h.visible));
const sessionIndividual = useSessionIndividual();
const sessionsStore = useSessionsStore();
const props = defineProps<{
  attendeeId: string;
  learningPathId: string;
}>();

paginate.setFetchFunction(async () => {
  if (props.attendeeId) {
    const paginateOptions = paginate.getFindOptions(paginate.paginateOptions.value);
    await fetchLearningPathAttendeeJourneyFullDatas(props.attendeeId, props.learningPathId, paginateOptions);
  }
});

async function loadDatas() {
  const { isLoading, isError } = await fetchWithStateHandling(async () => {
    await paginate.fetchData();
  });
  isLoadingRef.value = isLoading.value;
  isErrorRef.value = isError.value;
}

async function clickRow(params: any) {
  if (params.bookingId) {
    await sessionIndividual.loadSessionIndividual(
      {
        detailsId: "tr-" + params.bookingId,
        source: ItemType.Session,
      },
      false,
    );
  }
}

onMounted(async () => {
  await loadDatas();
});
</script>

<template>
  <div v-show="isLoadingRef">
    <IndexLayoutSkeleton :filters="1" :actions="2" :lane="9" add pagination />
  </div>
  <div v-show="isErrorRef && !isLoadingRef">
    Oups le contenu que vous souhaitez afficher n'est pas disponible, recharger la page ou alors contactez un
    administrateur afin de vérifier vos droits de compte
  </div>
  <div
    v-if="!isLoadingRef && !isErrorRef && !learningPathAttendeeJourneyFullDatas?.data?.length"
    class="flex flex-row items-center gap-4 bg-lightest-grey-color justify-center py-6 m-4 rounded-[12px]"
  >
    <SvgIcon name="target-menu" class="w-6 h-6 text-medium-grey-color"></SvgIcon>
    <div class="flex flex-col">
      <span class="body_regular text-medium-grey-color">Aucun parcours</span>
      <span class="body_regular text-medium-grey-color"> Aucun parcours n'a été effectué par cet utilisateur. </span>
    </div>
  </div>
  <IndexLayout
    v-else
    v-show="!isErrorRef && !isLoadingRef && learningPathAttendeeJourneyFullDatas?.data?.length"
    ref="indexGrid"
    :rows="learningPathAttendeeJourneyFullDatas as IPaginationApi<any>"
    track-by="learningPathAttendeeId"
    :headers="visibleHeaders"
    :nav="getNav()"
    :item-per-page="paginate.paginateOptions.value.limit as number"
    url="/training-course-attendee-journey?learningPathAttendeeId=__KEY__"
    :item-keys="['learningPathAttendeeId']"
    :resize="true"
    :pagination-key="PaginationKeysEnum.LEARNING_PATH_ATTENDEE_JOURNEY_PAGINATION"
    :page-container="false"
    :max-height="maxHeight"
    @page-navigate="paginate.pageNavigateWithoutSettings($event)"
    @tab-navigate="paginate.tabNavigateWithoutSettings($event)"
    @sort="paginate.sortColumnWithoutSettings($event)"
    @click-row="clickRow"
  >
    <template #row="{ row }">
      <AttendeeJourneyView :row="row"></AttendeeJourneyView>
    </template>
  </IndexLayout>
  <SessionAttendeeDetailsModal v-if="sessionsStore.showSessionModal"></SessionAttendeeDetailsModal>
</template>

<style lang="scss" scoped></style>
